/**
 * Sticky header, revealed on scroll up.
 *
 * The full header scrolls off the page like normal content. Once it is
 * completely out of view the header is pinned, hidden, in its simplified
 * stuck layout, slides in on any upward scroll and slides out again on
 * scroll down. Back at the top of the page the full header returns.
 *
 * Markup contract (see sections/header.liquid):
 *
 *   <div data-header-wrapper data-header-sticky-reveal data-header-transparent="true|false">
 *     <header data-header-height>...</header>
 *   </div>
 *
 * plus an optional [data-announcement-wrapper] above it. The script does
 * nothing when no [data-header-sticky-reveal] wrapper is on the page, which
 * is the case when 'Enable sticky header' is unticked.
 *
 * Why not the vendor sticky header
 * --------------------------------
 * Broadcast's Sticky class (features/header-sticky.js in theme.js.map) pins
 * the header as soon as the visitor scrolls past the announcement bar, in
 * every mode including 'directional', so the full header collapses near the
 * top of the page. It activates whenever [data-header-sticky] is present, so
 * the header outputs [data-header-sticky-reveal] instead. That leaves the
 * vendor class in its passive static mode and this script owns the stuck
 * state.
 *
 * The vendor classes are reused so the existing CSS still applies:
 * js__header__stuck (fixed, simplified layout from custom-header-huge.css),
 * js__header__stuck--animated (hidden, translateY(-100%)) and
 * js__header__stuck--trigger-animation (revealed, translateY(0)).
 *
 * Scroll position and direction come from the vendor 'theme:scroll' event,
 * which is already throttled with requestAnimationFrame.
 *
 * Pausing the reveal
 * ------------------
 * window.stickyHeaderReveal.pause() hides a revealed header and ignores
 * upward scroll until resume(). Sticking, unsticking and hiding carry on as
 * normal. The quick nav uses it so its own scroll up to a tab does not slide
 * the header in over the strip. Both calls do nothing without a sticky header.
 */

if (!window.stickyHeaderReveal) {
  const selectors = {
    wrapper: '[data-header-wrapper][data-header-sticky-reveal]',
    header: '[data-header-height]',
    announcement: '[data-announcement-wrapper]',
  };

  const classes = {
    stuck: 'js__header__stuck',
    hidden: 'js__header__stuck--animated',
    revealed: 'js__header__stuck--trigger-animation',
    instant: 'sticky-header-reveal--instant',
    hasStickyHeader: 'has-sticky-header',
  };

  const attributes = {
    transparent: 'data-header-transparent',
    scrollLocked: 'data-scroll-locked',
  };

  class StickyHeaderReveal {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.header = wrapper.querySelector(selectors.header);
      this.stuck = false;
      this.revealed = false;
      this.paused = false;
      this.fullBottom = 0;

      this.onScroll = (event) => this.update(event.detail);

      document.body.classList.add(classes.hasStickyHeader);
      document.addEventListener('theme:scroll', this.onScroll);

      this.update({ position: window.scrollY, up: false, down: false });
    }

    destroy() {
      document.removeEventListener('theme:scroll', this.onScroll);
      document.body.classList.remove(classes.hasStickyHeader);
    }

    get topOffset() {
      const announcement = document.querySelector(selectors.announcement);

      return announcement ? announcement.clientHeight : 0;
    }

    /* Page offset of the full header's bottom edge. Only valid while unstuck. */
    measure() {
      this.fullBottom = this.header.getBoundingClientRect().bottom + window.scrollY;
    }

    update({ position, up, down }) {
      // A drawer or modal is open, leave the header exactly as it is.
      if (document.documentElement.hasAttribute(attributes.scrollLocked)) return;

      if (!this.stuck) {
        this.measure();
        if (position <= this.fullBottom) return;
        this.stick();
      }

      if (down) this.hide();

      if (position <= this.topOffset || (!this.revealed && position <= this.fullBottom)) {
        this.unstick();
        return;
      }

      if (up && !this.paused) this.reveal();
    }

    /* Swap classes without the vendor slide transition, so nothing flashes on screen. */
    instantly(callback) {
      this.wrapper.classList.add(classes.instant);
      callback();
      void this.wrapper.offsetHeight;
      this.wrapper.classList.remove(classes.instant);
    }

    stick() {
      this.instantly(() => this.wrapper.classList.add(classes.stuck, classes.hidden));
      this.wrapper.setAttribute(attributes.transparent, false);
      this.stuck = true;
    }

    unstick() {
      this.instantly(() => this.wrapper.classList.remove(classes.stuck, classes.hidden, classes.revealed));
      this.wrapper.setAttribute(attributes.transparent, window.theme.settings.transparentHeader);
      this.stuck = false;
      this.revealed = false;
    }

    reveal() {
      if (this.revealed) return;
      this.wrapper.classList.add(classes.revealed);
      this.revealed = true;
    }

    hide() {
      if (!this.revealed) return;
      this.wrapper.classList.remove(classes.revealed);
      this.revealed = false;
    }
  }

  let instance = null;

  const init = () => {
    if (instance) instance.destroy();
    instance = null;

    const wrapper = document.querySelector(selectors.wrapper);
    if (wrapper) instance = new StickyHeaderReveal(wrapper);
  };

  const onSectionUnload = (event) => {
    if (instance && event.target.contains(instance.wrapper)) {
      instance.destroy();
      instance = null;
    }
  };

  const onSectionLoad = (event) => {
    if (event.target.querySelector('[data-header-wrapper]')) init();
  };

  const pause = () => {
    if (!instance) return;
    instance.paused = true;
    instance.hide();
  };

  const resume = () => {
    if (instance) instance.paused = false;
  };

  window.stickyHeaderReveal = { init, pause, resume };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:load', onSectionLoad);
}
